<template>
  <component :is="resolvedIcon" class="ui-icon" :size="size" :weight="weight" aria-hidden="true" />
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  PhArrowCircleUpRight,
  PhArrowCircleLeft,
  PhArrowsClockwise,
  PhArrowLeft,
  PhArrowRight,
  PhArrowsOutSimple,
  PhBaby,
  PhBellRinging,
  PhBarbell,
  PhCaretDown,
  PhCaretLeft,
  PhCaretRight,
  PhChartBar,
  PhChatCircleDots,
  PhCheck,
  PhCheckCircle,
  PhCheckFat,
  PhClockCounterClockwise,
  PhCopy,
  PhCursorClick,
  PhDotsThreeCircleVertical,
  PhDesktop,
  PhDownloadSimple,
  PhEye,
  PhEyeSlash,
  PhGearSix,
  PhHouse,
  PhInfo,
  PhImageSquare,
  PhLightning,
  PhLink,
  PhLinkSimple,
  PhLock,
  PhMagnifyingGlass,
  PhMoon,
  PhPackage,
  PhPaintBucket,
  PhPaperPlaneTilt,
  PhPencilSimple,
  PhPlus,
  PhPlusCircle,
  PhPlusSquare,
  PhCpu,
  PhSignOut,
  PhShoppingCart,
  PhSpinnerGap,
  PhSparkle,
  PhStar,
  PhStorefront,
  PhSun,
  PhTShirt,
  PhTextT,
  PhTrendUp,
  PhTrash,
  PhTelegramLogo,
  PhUserCircle,
  PhWarningCircle,
  PhWhatsappLogo,
  PhX,
  PhXCircle,
} from "@phosphor-icons/vue";
import type { Component } from "vue";

const props = withDefaults(
  defineProps<{
    name: string;
    size?: number | string;
    weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
  }>(),
  {
    size: 16,
    weight: "fill",
  },
);

const iconMap: Record<string, Component> = {
  ai: PhSparkle,
  analytics: PhChartBar,
  "arrow-circle-left": PhArrowCircleLeft,
  "arrow-circle-up-right": PhArrowCircleUpRight,
  "arrow-left": PhArrowLeft,
  "arrow-right": PhArrowRight,
  "arrows-clockwise": PhArrowsClockwise,
  baby: PhBaby,
  barbell: PhBarbell,
  "bold-check-mark": PhCheckFat,
  "bell-ringing": PhBellRinging,
  box: PhPackage,
  chat: PhChatCircleDots,
  "check-mark": PhCheck,
  "check-circle": PhCheckCircle,
  "chevron-down": PhCaretDown,
  "chevron-left": PhCaretLeft,
  "chevron-right": PhCaretRight,
  "clock-counter-clockwise": PhClockCounterClockwise,
  copy: PhCopy,
  cross: PhX,
  "x-circle": PhXCircle,
  "cursor-click": PhCursorClick,
  desktop: PhDesktop,
  download: PhDownloadSimple,
  expand: PhArrowsOutSimple,
  eye: PhEye,
  "eye-slash": PhEyeSlash,
  house: PhHouse,
  info: PhInfo,
  "image-square": PhImageSquare,
  layers: PhPackage,
  lightning: PhLightning,
  lock: PhLock,
  link: PhLinkSimple,
  "link-h": PhLink,
  "loading-line": PhSpinnerGap,
  moon: PhMoon,
  pen: PhPencilSimple,
  "paper-plane": PhPaperPlaneTilt,
  "photo-magic-wand": PhSparkle,
  "photo-paint-bucket": PhPaintBucket,
  "photo-sparkle": PhSparkle,
  "photo-text-t": PhTextT,
  plus: PhPlus,
  "plus-circle": PhPlusCircle,
  "plus-rounded": PhPlus,
  cpu: PhCpu,
  search: PhMagnifyingGlass,
  settings: PhGearSix,
  "sign-out": PhSignOut,
  "shopping-cart": PhShoppingCart,
  storefront: PhStorefront,
  sun: PhSun,
  "squared-plus": PhPlusSquare,
  star: PhStar,
  "text-t": PhTextT,
  tshirt: PhTShirt,
  "three-dots-v": PhDotsThreeCircleVertical,
  "dots-three-circle-vertical": PhDotsThreeCircleVertical,
  "telegram-logo": PhTelegramLogo,
  "trending-up": PhTrendUp,
  trash: PhTrash,
  user: PhUserCircle,
  "warning-circle": PhWarningCircle,
  "whatsapp-logo": PhWhatsappLogo,
};

const resolvedIcon = computed<Component>(() => iconMap[props.name] ?? PhSparkle);
</script>

<style scoped>
.ui-icon {
  display: inline-block;
  vertical-align: middle;
}
</style>
